"use client";
import { StrapiMetaData } from "@/types/strapi/components/shared/meta";
import { SectionReviews, SectionReviewsProps } from "@/components/shared/sections/SectionReviews";
import { SamplePageSampleCardProps } from "@/components/pages/samples/SamplePageSampleCard";
import { useDispatch } from "react-redux";
import { useCallback, useEffect } from "react";
import { setBodyOverflow } from "@/store/slices/globalSlice";
import { academicDisciplines } from "@/content/samples/_academic-disciplines";
import { paperTypes } from "@/content/samples/_paper-types";
import { academicLevels } from "@/content/samples/_academic-levels";
import { citationStyles } from "@/content/samples/_citation-styles";
import { samplesSlice } from "@/store/slices/samplesSlice";
import { filterSamples, showSamples } from "@/store/actions/samplesActions";
import { SectionSamples } from "@/components/pages/samples/SectionSamples";
import { SectionPromo } from "@/components/shared/sections/SectionPromo";

type SamplesPageProps = {
  content: {
    meta: StrapiMetaData;
    sectionPromo: {
      heading: string;
      subheading: string;
    };
    sectionReviews: SectionReviewsProps;
  };
  samplesList: (Omit<SamplePageSampleCardProps, "onClick"> & { id: string })[];
};

export const SamplesPage = ({ content, samplesList }: SamplesPageProps) => {
  const { sectionPromo, sectionReviews } = content;
  const { reviews, heading, subheading } = sectionReviews;

  const dispatch = useDispatch();

  const removeBodyOverflow = useCallback(() => {
    dispatch(setBodyOverflow(false));
    document.body.classList.remove("overflowHidden");
  }, [dispatch]);

  useEffect(() => {
    const availableSubjects = academicDisciplines.filter(({ id }) =>
      samplesList.map(({ idAcademicDiscipline }) => idAcademicDiscipline).includes(id)
    );
    const availableTypes = paperTypes.filter(({ id }) =>
      samplesList.map(({ idPaperType }) => idPaperType).includes(id)
    );
    const availableLevels = academicLevels.filter(({ id }) =>
      samplesList.map(({ idAcademicLevel }) => idAcademicLevel).includes(id)
    );
    const availableFormats = citationStyles.filter(({ id }) =>
      samplesList.map(({ idCitationStyle }) => idCitationStyle).includes(id)
    );

    dispatch(
      samplesSlice.actions.setInitialValues({
        list: samplesList,
        filters: {
          idAcademicDisciplines: availableSubjects,
          idPaperTypes: availableTypes,
          idAcademicLevels: availableLevels,
          idCitationStyles: availableFormats,
        },
      })
    );
    dispatch(showSamples());
    dispatch(filterSamples());
  }, [samplesList, dispatch]);

  useEffect(() => removeBodyOverflow, [removeBodyOverflow]);

  return (
    <>
      <SectionPromo
        heading={sectionPromo.heading}
        subheading={sectionPromo.subheading}
        image={{
          src: "/img/pages/samples/book@2x.png",
          width: 110,
          height: 110,
        }}
        breadcrumbs={[
          { position: 1, name: "Main", item: "/" },
          {
            position: 2,
            name: "Samples",
            item: "/samples",
          },
        ]}
      />
      <SectionSamples samplesList={samplesList} />
      <SectionReviews reviews={reviews} heading={heading} subheading={subheading} />
    </>
  );
};
